<template>
  <div>
    <div
      class="d-flex flex-wrap align-items-center gap-2 mb-3"
      data-testid="picker-toolbar"
    >
      <div class="form-check mb-0">
        <input
          :id="autoId"
          v-model="autoDistribute"
          class="form-check-input"
          type="checkbox"
        />
        <label class="form-check-label text-nowrap" :for="autoId">
          {{ $t("forwards.picker.take.auto_distribute") }}
        </label>
      </div>
      <div class="form-check mb-0">
        <input
          :id="spreadId"
          v-model="spread"
          class="form-check-input"
          type="checkbox"
        />
        <label class="form-check-label text-nowrap" :for="spreadId">
          {{ $t("forwards.picker.take.spread") }}
        </label>
      </div>
      <div class="picker-target">
        <MwhInputComponent
          v-model="target"
          :id="targetId"
          size="sm"
          :ariaLabel="$t('forwards.picker.take.target_label')"
          :placeholder="$t('forwards.picker.take.target')"
        />
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="takenPoolIds.length === 0"
        @click="clearTakes"
      >
        {{ $t("forwards.picker.take.clear") }}
      </button>
      <LoadingIndicatorComponent
        v-if="loading && rows.length > 0"
        class="ms-auto"
        inline
        :message="$t('forwards.picker.take.refreshing')"
      />
    </div>

    <p v-if="floorHint" class="advisory-note mb-3" data-testid="picker-hint">
      <FontAwesomeIcon icon="fa-solid fa-circle-info" />
      <span>{{ floorHint }}</span>
    </p>

    <div
      v-if="allWarnings.length > 0"
      class="advisory-note mb-3"
      data-testid="picker-warnings"
    >
      <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" />
      <div>
        <strong>{{ $t("forwards.picker.take.warnings_title") }}</strong>
        <ul class="mb-0 ps-3">
          <li v-for="warning in allWarnings" :key="warning">{{ warning }}</li>
        </ul>
      </div>
    </div>

    <div v-if="errorMessage" class="error-note mb-3" role="alert">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ errorMessage }}</span>
      <button
        type="button"
        class="btn btn-sm btn-link ms-auto p-0"
        @click="emit('retry')"
      >
        {{ $t("forwards.common.retry") }}
      </button>
    </div>

    <!-- A reload keeps the table on screen: replacing it with the spinner would tear the row the
         trader is typing into out from under the caret (FIX4-4 point 4). -->
    <LoadingIndicatorComponent
      v-if="loading && rows.length === 0"
      :message="$t('forwards.picker.take.loading')"
    />

    <div v-else class="table-responsive" :aria-busy="loading">
      <DataTable
        ref="takeTable"
        v-model:selection="selectedRows"
        v-model:filters="filters"
        :value="tableRows"
        :selectAll="allVisibleSelected"
        @select-all-change="selectVisibleRows"
        dataKey="pool_id"
        class="border rounded"
        filterDisplay="row"
        sortField="name"
        :sortOrder="1"
        resizableColumns
        columnResizeMode="fit"
        @column-resize-end="onColumnResizeEnd"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">
            {{ $t("forwards.picker.take.empty") }}
          </div>
        </template>

        <Column
          selectionMode="multiple"
          headerStyle="width: 1.5rem"
          :pt="{ columnResizer: { class: 'd-none' } }"
        />

        <Column
          field="name"
          filterField="pool_search"
          :header="$t('forwards.picker.take.pool')"
          :style="columnStyle('pool')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #sorticon="{ sortOrder }">
            <font-awesome-icon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <div class="fw-semibold" :title="poolSubtitle(data)">
              <PoolLinkComponent :poolId="data.pool_id">{{
                data.name
              }}</PoolLinkComponent>
            </div>
            <div
              v-if="heldBackNote(data)"
              class="text-body-tertiary fs-10"
              data-testid="picker-held-back"
            >
              {{ heldBackNote(data) }}
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter w-100"
              :placeholder="$t('forwards.picker.take.filter_pool')"
              @input="filterCallback()"
            />
          </template>
        </Column>

        <Column
          field="capacity_total"
          :header="$t('forwards.picker.take.take')"
          :style="columnStyle('take')"
          :headerStyle="{ minWidth: '130px' }"
          sortable
          dataType="numeric"
        >
          <template #sorticon="{ sortOrder }">
            <font-awesome-icon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <div class="picker-take-input">
              <input
                type="number"
                class="form-control form-control-sm d-inline-block text-end"
                :class="{
                  'is-invalid':
                    (takes[data.pool_id] ?? 0) > data.capacity_total,
                }"
                :value="takes[data.pool_id] ?? ''"
                :disabled="!(takes[data.pool_id] > 0)"
                min="0"
                :max="data.capacity_total"
                step="1"
                :placeholder="formatNumberWithSpaces(data.capacity_total)"
                :title="takeTooltip(data)"
                :aria-label="capacityCapLabel(data.capacity_total)"
                @input="setTakeFromInput(data.pool_id, $event)"
                @change="commitTakeInput(data.pool_id, $event)"
              />
            </div>
          </template>
        </Column>

        <Column
          field="technologies"
          :header="$t('forwards.picker.take.technology')"
          :style="columnStyle('technology')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            {{ technologyLabels(data.technologies) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="technologyOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <Column
          field="countries"
          :header="$t('forwards.picker.take.country')"
          :style="columnStyle('country')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            <CountryPillComponent
              v-if="data.countries.length > 0"
              :country="data.countries"
            />
            <span v-else class="text-body-tertiary">
              {{ $t("forwards.common.unknown") }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="countryOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <Column
          field="oldest_cod"
          :header="$t('forwards.picker.take.cod')"
          :style="columnStyle('cod')"
          sortable
        >
          <template #sorticon="{ sortOrder }">
            <font-awesome-icon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            {{ data.oldest_cod ? formatDate(data.oldest_cod) : "—" }}
          </template>
        </Column>

        <Column
          field="ecolabels"
          :header="$t('forwards.picker.take.ecolabels')"
          :style="columnStyle('ecolabels')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            <span v-if="data.ecolabels.length" class="d-flex gap-1 flex-wrap">
              <span
                v-for="key in data.ecolabels"
                :key="key"
                class="badge rounded-pill picker-ecolabel-badge"
                :class="ecolabelBadgeClass(key)"
              >
                {{ ecolabelBrandName(key) }}
              </span>
            </span>
            <span v-else class="text-body-tertiary">
              {{ $t("forwards.common.none") }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="ecolabelOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <Column
          field="support"
          :header="$t('forwards.picker.take.support')"
          :style="columnStyle('support')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            {{ $t(`forwards.common.support.${data.support}`) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="supportOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <Column
          field="minimum_price"
          filterField="conditions"
          :header="$t('forwards.picker.take.conditions')"
          :style="columnStyle('conditions')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
        >
          <template #body="{ data }">
            <div>{{ floorLabel(data.minimum_price) }}</div>
            <div
              v-if="data.contango_condition !== 'none'"
              class="text-warning-emphasis fs-10"
            >
              {{
                $t(`forwards.picker.take.contango.${data.contango_condition}`)
              }}
            </div>
            <div
              v-if="data.cadence_warning"
              class="text-warning-emphasis fs-10"
            >
              {{ $t("forwards.picker.take.cadence") }}
            </div>
            <div
              v-if="data.allocation_ready_warning.length > 0"
              class="text-warning-emphasis fs-10"
            >
              {{
                $t("forwards.picker.take.allocation_not_ready", {
                  companies: data.allocation_ready_warning.join(", "),
                })
              }}
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="conditionOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <details
      v-if="ineligible.length > 0"
      class="mt-3"
      data-testid="picker-ineligible"
    >
      <summary class="fs-10 text-body-tertiary">
        {{
          $t("forwards.picker.take.ineligible_title", {
            count: ineligible.length,
          })
        }}
      </summary>
      <p class="fs-10 text-body-tertiary mt-2 mb-1">
        {{ $t("forwards.picker.take.ineligible_hint") }}
      </p>
      <ul class="fs-10 text-body-tertiary mb-0 ps-3">
        <li v-for="pool in ineligible" :key="pool.pool_id">
          <strong>{{ pool.name }}</strong> — {{ ineligibleReasons(pool) }}
        </li>
      </ul>
    </details>
  </div>
</template>

<script setup lang="ts">
import { ecolabelBadgeClass, ecolabelBrandName } from "@/utils/ecolabelBadges";
import { getSortIcon } from "@/utils/sortIcons";
import ColumnMultiSelectFilter from "@/components/common/ColumnMultiSelectFilter.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import { usePersistedColumnWidths } from "@/composables/usePersistedColumnWidths";
import CountryPillComponent from "@/forwards2/components/CountryPillComponent.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import PoolLinkComponent from "@/forwards2/components/PoolLinkComponent.vue";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import {
  CONTANGO_CONDITIONS,
  POOL_SUPPORTS,
  type ContangoCondition,
  type IneligiblePool,
  type MinimumPriceHint,
  type PickerPoolRow,
} from "@/forwards2/forwardsTypes";
import {
  distributeTakes,
  type TakeMap,
} from "@/forwards2/picker/distributeTakes";
import { useSalesStrategyText } from "@/forwards2/salesStrategy";
import { technologyName } from "@/types/technology";
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import { debounce, sortBy, uniq } from "lodash-es";
import Column from "primevue/column";
import DataTable, { type DataTableFilterMeta } from "primevue/datatable";
import InputText from "primevue/inputtext";
import { computed, onUnmounted, ref, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Step three: how much to take from each pool.
 *
 * The table is the spot sale's portfolio-item table one idiom at a time
 * (`PortfolioItemsTable.vue`): per-column filters in the header row rather than one search box, the
 * take as a plain compact number input whose placeholder is what the pool has left, secondary detail
 * on tooltips rather than second lines, and the same FontAwesome sort icon.
 *
 * The toolbar is the spot sale's auto-select, one word at a time (plan §1.1): while
 * "Auto-distribute" is ticked the distribution re-runs, debounced, whenever the target, the spread
 * toggle, the filters or the rows change, and it runs at once when the box is ticked. Typing a take
 * by hand does not untick it — the next re-run simply overwrites the row, exactly as the spot table
 * behaves.
 *
 * Distribution is scoped to the rows the header filters leave on screen: takes on rows that are
 * filtered out are carried over, so a trader can take from one country, filter to another and take
 * again.
 *
 * A row whose sales strategy holds volume back says how much and when the next window opens; a row
 * is capped at what the strategy has released (`capacity_*`): the picker never takes past it.
 */
const props = withDefaults(
  defineProps<{
    rows: PickerPoolRow[];
    /** The picked period labels, in order — the take cell's tooltip follows them. */
    periods: string[];
    ineligible?: IneligiblePool[];
    productWarnings?: string[];
    hint?: MinimumPriceHint | null;
    loading?: boolean;
    error?: unknown;
  }>(),
  {
    ineligible: () => [],
    productWarnings: () => [],
    hint: null,
    loading: false,
  }
);

const emit = defineEmits<{
  retry: [];
  /** The rows the header filters leave on screen, so the summary column can count the same set. */
  "update:rowsInScope": [rows: PickerPoolRow[]];
}>();

const takes = defineModel<TakeMap>({ required: true });

const { t } = useI18n();
const strategyText = useSalesStrategyText();

const AUTO_DISTRIBUTE_DEBOUNCE_MS = 500;

/** The "nothing here" option of a filter, as the spot table's ecolabel and price filters spell it. */
const NO_VALUE = "__none__";

const contangoKey = (condition: ContangoCondition) =>
  `__contango_${condition}__`;
const CONTANGO_KEYS = CONTANGO_CONDITIONS.map(contangoKey);

interface FilterOption {
  label: string;
  value: string;
}

interface PoolConditions {
  minimum_price: string | null;
  contango_condition: ContangoCondition;
}

/**
 * A row plus the fields its filters match. PrimeVue matches a column filter against one named field
 * of the row: the Pool filter has to reach the member companies as well as the pool's name, and the
 * Conditions filter both the floor and the contango condition.
 */
interface PickerTableRow extends PickerPoolRow {
  pool_search: string;
  conditions: PoolConditions;
}

/** Any of the selected keys is in the row's list; `__none__` matches a row with an empty list. */
FilterService.register(
  "pickerAnyOf",
  (value: string[] | null, selected: string[] | null): boolean => {
    if (!selected?.length) return true;
    if (!value?.length) return selected.includes(NO_VALUE);
    return selected.some((key) => key !== NO_VALUE && value.includes(key));
  }
);

/**
 * The floor keys and the contango keys filter independently: a group with nothing selected in it
 * passes every row, so picking only a floor ignores the contango condition and vice versa. Within a
 * group the options are alternatives: a pool has one floor and one contango condition.
 */
FilterService.register(
  "pickerConditions",
  (conditions: PoolConditions, selected: string[] | null): boolean => {
    const floors = (selected ?? []).filter(
      (key) => !CONTANGO_KEYS.includes(key)
    );
    const contango = (selected ?? []).filter((key) =>
      CONTANGO_KEYS.includes(key)
    );
    const floorMatches =
      floors.length === 0 ||
      (conditions.minimum_price === null
        ? floors.includes(NO_VALUE)
        : floors.includes(conditions.minimum_price));
    const contangoMatches =
      contango.length === 0 ||
      contango.includes(contangoKey(conditions.contango_condition));
    return floorMatches && contangoMatches;
  }
);

const autoId = useId();
const spreadId = useId();
const targetId = useId();

const autoDistribute = ref(false);
const spread = ref(false);
const target = ref<number | null>(null);

const filters = ref<DataTableFilterMeta>({
  pool_search: { value: null, matchMode: FilterMatchMode.CONTAINS },
  technologies: { value: [], matchMode: "pickerAnyOf" },
  countries: { value: [], matchMode: "pickerAnyOf" },
  ecolabels: { value: [], matchMode: "pickerAnyOf" },
  support: { value: [], matchMode: FilterMatchMode.IN },
  conditions: { value: [], matchMode: "pickerConditions" },
});

const { columnStyle, onColumnResizeEnd } = usePersistedColumnWidths(
  "forwards2.picker-take.v1",
  [
    null,
    "pool",
    "take",
    "technology",
    "country",
    "cod",
    "ecolabels",
    "support",
    "conditions",
  ]
);

const takeTable = useTemplateRef<{ processedData?: PickerTableRow[] }>(
  "takeTable"
);

const tableRows = computed<PickerTableRow[]>(() =>
  props.rows.map((row) => ({
    ...row,
    pool_search: [row.name, ...row.member_company_names].join(" "),
    conditions: {
      minimum_price: row.minimum_price,
      contango_condition: row.contango_condition,
    },
  }))
);

const technologyOptions = computed(() =>
  optionsOf(
    props.rows.flatMap((row) => row.technologies),
    technologyName
  )
);

/** The country codes as the pills show them, so the filter reads like the column it sits under. */
const countryOptions = computed(() =>
  optionsOf(
    props.rows.flatMap((row) => row.countries),
    (code) => code
  )
);

const ecolabelOptions = computed<FilterOption[]>(() => [
  ...optionsOf(
    props.rows.flatMap((row) => row.ecolabels),
    ecolabelBrandName
  ),
  { label: t("forwards.common.none"), value: NO_VALUE },
]);

const supportOptions = computed<FilterOption[]>(() =>
  POOL_SUPPORTS.map((support) => ({
    label: t(`forwards.common.support.${support}`),
    value: support,
  }))
);

const conditionOptions = computed<FilterOption[]>(() => [
  ...sortBy(
    uniq(
      props.rows
        .map((row) => row.minimum_price)
        .filter((price): price is string => price !== null)
    ),
    Number
  ).map((price) => ({ label: floorLabel(price), value: price })),
  { label: t("forwards.picker.take.no_floor"), value: NO_VALUE },
  ...CONTANGO_CONDITIONS.map((condition) => ({
    label: t(`forwards.picker.take.contango_filter.${condition}`),
    value: contangoKey(condition),
  })),
]);

const selectedRows = computed({
  get: () =>
    tableRows.value.filter((row) => (takes.value[row.pool_id] ?? 0) > 0),
  set: (rows: PickerTableRow[]) => {
    takes.value = Object.fromEntries(
      rows
        .filter((row) => row.capacity_total > 0)
        .map((row) => [
          row.pool_id,
          takes.value[row.pool_id] || row.capacity_total,
        ])
    );
  },
});

const takenPoolIds = computed(() =>
  Object.entries(takes.value)
    .filter(([, volume]) => volume > 0)
    .map(([poolId]) => Number(poolId))
);

const floorHint = computed(() =>
  props.hint?.floor
    ? t("forwards.picker.take.hint_floor", { value: props.hint.floor })
    : null
);

const allWarnings = computed(() => [
  ...props.productWarnings,
  ...(props.hint?.warnings ?? []),
]);

const errorMessage = computed(() => {
  if (props.error === null || props.error === undefined) return null;
  return (
    forwardsErrorMessage(props.error) ?? t("forwards.picker.take.load_failed")
  );
});

/**
 * The rows the header filters leave on screen, read off the table itself — the spot table's
 * `getFilteredItems` (`PortfolioItemsTable.vue:1631`). Falls back to every row before the table has
 * mounted.
 */
const rowsInScope = (): PickerTableRow[] => {
  const processed = takeTable.value?.processedData;
  return Array.isArray(processed) ? processed : tableRows.value;
};

// `processedData` is a computed on the DataTable instance, so reading it through the template ref
// tracks it: the watcher re-publishes the scope whenever a header filter or the row set changes.
const scopedRows = computed(rowsInScope);

watch(scopedRows, (rows) => emit("update:rowsInScope", rows), {
  immediate: true,
});

const allVisibleSelected = computed(() => {
  const rows = rowsInScope().filter((row) => row.capacity_total > 0);
  return (
    rows.length > 0 && rows.every((row) => (takes.value[row.pool_id] ?? 0) > 0)
  );
});

const selectVisibleRows = ({ checked }: { checked: boolean }) => {
  const next = { ...takes.value };
  for (const row of rowsInScope()) {
    if (checked && row.capacity_total > 0)
      next[row.pool_id] ||= row.capacity_total;
    else delete next[row.pool_id];
  }
  takes.value = next;
};

const runDistribution = () => {
  if (!autoDistribute.value || target.value === null) return;
  takes.value = distributeTakes(
    rowsInScope(),
    target.value,
    spread.value,
    takes.value
  );
};

const debouncedDistribution = debounce(
  runDistribution,
  AUTO_DISTRIBUTE_DEBOUNCE_MS
);

const redistribute = () => {
  if (autoDistribute.value) debouncedDistribution();
};

watch(autoDistribute, (enabled) => {
  if (!enabled) return;
  // Ticking the box is a deliberate act and answers immediately; every later change waits.
  debouncedDistribution.cancel();
  runDistribution();
});

watch([target, spread, tableRows], redistribute);

watch(filters, redistribute, { deep: true });

onUnmounted(() => debouncedDistribution.cancel());

const setTake = (poolId: number, volume: number | null) => {
  const next = { ...takes.value };
  if (volume === null || volume <= 0) {
    delete next[poolId];
  } else {
    next[poolId] = volume;
  }
  takes.value = next;
};

// Keep the row enabled while its count is being replaced; blur/change commits an empty value.
const setTakeFromInput = (poolId: number, event: Event) => {
  if (!(takes.value[poolId] > 0)) return;
  const parsed = Number((event.target as HTMLInputElement).value);
  if (Number.isFinite(parsed) && parsed > 0)
    setTake(poolId, Math.round(parsed));
};

const commitTakeInput = (poolId: number, event: Event) => {
  if (!(takes.value[poolId] > 0)) return;
  const raw = (event.target as HTMLInputElement).value.trim();
  const parsed = Number(raw);
  setTake(
    poolId,
    raw === "" || !Number.isFinite(parsed) ? null : Math.round(parsed)
  );
};

const clearTakes = () => {
  takes.value = {};
};

const capacityCapLabel = (capacity: number): string =>
  t("forwards.picker.take.capacity_cap", {
    value: formatNumberWithSpaces(capacity),
  });

/** The cap the field is checked against, then what is available in each picked period. */
const takeTooltip = (row: PickerPoolRow): string =>
  [
    capacityCapLabel(row.capacity_total),
    ...props.periods.map((period) =>
      t("forwards.picker.take.capacity_period", {
        period,
        value: formatNumberWithSpaces(row.capacity_by_period[period] ?? 0),
      })
    ),
  ].join("\n");

/** What the strategy holds back today, and when more is released; null when it holds nothing back. */
const heldBackNote = (row: PickerPoolRow): string | null => {
  if (row.held_back_mwh <= 0) return null;
  const values = {
    mwh: formatNumberWithSpaces(row.held_back_mwh),
    strategy: strategyText.summary(row.sales_plan),
  };
  return row.next_release === null
    ? t("forwards.sales_strategy.held_back", values)
    : t("forwards.sales_strategy.held_back_next_release", {
        ...values,
        date: strategyText.releaseDate(row.next_release.opens_on),
        next: formatNumberWithSpaces(row.next_release.added_mwh),
      });
};

/** The backend's reasons, or, for a pool its sales strategy holds back, when that ends. */
const ineligibleReasons = (pool: IneligiblePool): string =>
  pool.held_back_until === null
    ? pool.reasons.join("; ")
    : strategyText.heldBackReason(pool.held_back_until);

const poolSubtitle = (row: PickerPoolRow): string =>
  t("forwards.picker.take.row_subtitle", {
    companies: row.member_company_names.join(", "),
    devices: t("forwards.common.device_count", row.device_count),
  });

const floorLabel = (minimumPrice: string | null): string =>
  minimumPrice === null
    ? t("forwards.picker.take.no_floor")
    : t("forwards.picker.take.floor_value", { value: minimumPrice });

const technologyLabels = (technologies: string[]): string =>
  technologies.map(technologyName).join(", ") || "—";

/** The distinct values the rows carry for one column, labelled and ordered for a filter select. */
const optionsOf = (
  values: string[],
  labelOf: (value: string) => string
): FilterOption[] =>
  sortBy(
    uniq(values).map((value) => ({ label: labelOf(value), value })),
    (option) => option.label
  );
</script>

<style scoped lang="scss">
.picker-target {
  width: 9rem;
}

// The spot table's compact pill (PortfolioItemsTable `.badge-compact`).
.picker-ecolabel-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
}

.picker-take-input {
  width: 7rem;
  // Right-aligned in its cell, like the spot table's count input, so it reads with the numbers
  // beside it rather than with the identity columns.
  margin-left: auto;
}
</style>
